import { makeAutoObservable } from 'mobx';

import { PhotoArtifactTab } from '../../types.ts/story';
import { updateStoryShowcase } from '../../utility/story';

import type {
  Artifact,
  Showcase,
  ShowcaseArtifactsAiTitleUpdate,
  ShowcaseArtifactsVideoUpdate,
  Story,
  StoryArtifactsAiTitleUpdate,
  StoryArtifactsVideoUpdate,
} from '../../types.ts/story';
import type VideoCreatorStore from '../VideoCreatorStore';

class StatusUpdateManager {
  store: VideoCreatorStore;

  watchedStoryArtifacts: Record<Story['id'], Pick<Artifact, 'id' | 'title'>[]> =
    {};
  watchedOrganizationArtifacts: Record<
    Showcase['id'],
    Pick<Artifact, 'id' | 'title'>[]
  > = {};

  // Video artifacts tracking for updates
  watchedStoryVideoArtifacts: Record<
    Story['id'],
    Pick<Artifact, 'id' | 'url'>[]
  > = {};
  watchedOrganizationVideoArtifacts: Record<
    Showcase['id'],
    Pick<Artifact, 'id' | 'url'>[]
  > = {};

  constructor(store: VideoCreatorStore) {
    makeAutoObservable(this, {
      store: false,
    });
    this.store = store;
  }

  getStoryIdsWithWatchedArtifacts(): string[] {
    return Object.keys(this.watchedStoryArtifacts);
  }

  getOrganizationIdsWithWatchedArtifacts(): string[] {
    return Object.keys(this.watchedOrganizationArtifacts);
  }

  getStoryIdsWithWatchedVideoArtifacts(): string[] {
    return Object.keys(this.watchedStoryVideoArtifacts);
  }

  getOrganizationIdsWithWatchedVideoArtifacts(): string[] {
    return Object.keys(this.watchedOrganizationVideoArtifacts);
  }

  addStoryArtifactsToWatch(storyId: string, artifacts: Artifact[]) {
    if (!this.watchedStoryArtifacts[storyId]) {
      this.watchedStoryArtifacts[storyId] = [];
    }
    this.watchedStoryArtifacts[storyId].push(
      ...artifacts.map(a => ({
        id: a.id,
        title: a.title,
      })),
    );
  }

  addOrganizationArtifactsToWatch(
    organizationId: string,
    artifacts: Artifact[],
  ) {
    if (!this.watchedOrganizationArtifacts[organizationId]) {
      this.watchedOrganizationArtifacts[organizationId] = [];
    }
    this.watchedOrganizationArtifacts[organizationId].push(
      ...artifacts.map(a => ({
        id: a.id,
        title: a.title,
      })),
    );
  }

  addStoryVideoArtifactsToWatch(storyId: string, artifacts: Artifact[]) {
    if (!this.watchedStoryVideoArtifacts[storyId]) {
      this.watchedStoryVideoArtifacts[storyId] = [];
    }
    const artifactsToWatch = artifacts.map(a => ({
      id: a.id,
      url: a.url,
    }));
    this.watchedStoryVideoArtifacts[storyId].push(...artifactsToWatch);
  }

  addOrganizationVideoArtifactsToWatch(
    organizationId: string,
    artifacts: Artifact[],
  ) {
    if (!this.watchedOrganizationVideoArtifacts[organizationId]) {
      this.watchedOrganizationVideoArtifacts[organizationId] = [];
    }
    const artifactsToWatch = artifacts.map(a => ({
      id: a.id,
      url: a.url,
    }));
    this.watchedOrganizationVideoArtifacts[organizationId].push(
      ...artifactsToWatch,
    );
  }

  updateWatchedStoryArtifacts(updates: StoryArtifactsAiTitleUpdate[]) {
    if (!this.store.story) {
      return;
    }
    for (const storyUpdate of updates) {
      if (this.store.story.id !== storyUpdate.id) {
        continue;
      }
      for (const artifactUpdate of storyUpdate.storyArtifacts) {
        const watchedArtifact = this.watchedStoryArtifacts[
          storyUpdate.id
        ]?.find(a => a.id === artifactUpdate.id);
        if (
          !watchedArtifact ||
          watchedArtifact.title === artifactUpdate.title ||
          !artifactUpdate.title
        ) {
          continue;
        }
        this.updateWatchedStoryArtifact(artifactUpdate);
        this.removeStoryArtifactFromWatched(storyUpdate.id, artifactUpdate.id);
      }
    }
  }

  updateWatchedOrganizationArtifacts(
    updates: ShowcaseArtifactsAiTitleUpdate[],
  ) {
    if (!this.store.organization) {
      return;
    }
    for (const organizationUpdate of updates) {
      if (this.store.organization.id !== organizationUpdate.id) {
        continue;
      }
      for (const artifactUpdate of organizationUpdate.organizationArtifacts) {
        const watchedArtifact = this.watchedOrganizationArtifacts[
          organizationUpdate.id
        ]?.find(a => a.id === artifactUpdate.id);
        if (
          !watchedArtifact ||
          watchedArtifact.title === artifactUpdate.title ||
          !artifactUpdate.title
        ) {
          continue;
        }
        this.updateWatchedOrganizationArtifact(artifactUpdate);
        this.removeOrganizationArtifactFromWatched(
          organizationUpdate.id,
          artifactUpdate.id,
        );
      }
    }
    if (this.store.story) {
      updateStoryShowcase(this.store.story, this.store.organization);
    }
  }

  updateWatchedStoryVideoArtifacts(updates: StoryArtifactsVideoUpdate[]) {
    if (!this.store.story) {
      return;
    }
    for (const storyUpdate of updates) {
      if (this.store.story.id !== storyUpdate.id) {
        continue;
      }
      for (const artifactUpdate of storyUpdate.storyArtifactsVideo) {
        const watchedArtifact = this.watchedStoryVideoArtifacts[
          storyUpdate.id
        ]?.find(a => a.id === artifactUpdate.id);
        if (!watchedArtifact || !artifactUpdate.video?.muxPlaybackId) {
          continue;
        }
        this.updateWatchedStoryVideoArtifact(artifactUpdate);
        this.removeStoryVideoArtifactFromWatched(
          storyUpdate.id,
          artifactUpdate.id,
        );
      }
    }
  }

  updateWatchedOrganizationVideoArtifacts(
    updates: ShowcaseArtifactsVideoUpdate[],
  ) {
    if (!this.store.organization) {
      return;
    }
    for (const organizationUpdate of updates) {
      if (this.store.organization.id !== organizationUpdate.id) {
        continue;
      }
      for (const artifactUpdate of organizationUpdate.organizationArtifactsVideo) {
        const watchedArtifact = this.watchedOrganizationVideoArtifacts[
          organizationUpdate.id
        ]?.find(a => a.id === artifactUpdate.id);
        if (!watchedArtifact || !artifactUpdate.video?.muxPlaybackId) {
          continue;
        }
        this.updateWatchedOrganizationVideoArtifact(artifactUpdate);
        this.removeOrganizationVideoArtifactFromWatched(
          organizationUpdate.id,
          artifactUpdate.id,
        );
      }
    }
    if (this.store.story) {
      updateStoryShowcase(this.store.story, this.store.organization);
    }
  }

  private updateWatchedStoryArtifact(
    artifactUpdate: StoryArtifactsAiTitleUpdate['storyArtifacts'][0],
  ) {
    if (!this.store.story?.storyArtifacts) {
      return;
    }
    const artifactIdx = this.store.story.storyArtifacts!.findIndex(
      a => a.id === artifactUpdate.id,
    );
    if (artifactIdx > -1) {
      this.store.story.storyArtifacts = this.store.story.storyArtifacts.map(
        a =>
          a.id === artifactUpdate.id
            ? { ...a, title: artifactUpdate.title }
            : a,
      );
    }
    if (this.store.selectedPhotoAssets.tab === PhotoArtifactTab.story) {
      this.updateSelectedPhotoAssetsResource(artifactUpdate);
    }
  }

  private updateWatchedOrganizationArtifact(
    artifactUpdate: ShowcaseArtifactsAiTitleUpdate['organizationArtifacts'][0],
  ) {
    if (!this.store.organization?.organizationArtifacts) {
      return;
    }
    const artifactIdx =
      this.store.organization.organizationArtifacts!.findIndex(
        a => a.id === artifactUpdate.id,
      );
    if (artifactIdx > -1) {
      this.store.organization.organizationArtifacts =
        this.store.organization.organizationArtifacts.map(a =>
          a.id === artifactUpdate.id
            ? { ...a, title: artifactUpdate.title }
            : a,
        );
    }
    if (this.store.selectedPhotoAssets.tab === PhotoArtifactTab.album) {
      this.updateSelectedPhotoAssetsResource(artifactUpdate);
    }
  }

  private updateSelectedPhotoAssetsResource(
    artifactUpdate: StoryArtifactsAiTitleUpdate['storyArtifacts'][0],
  ) {
    this.store.selectedPhotoAssets.resource =
      this.store.selectedPhotoAssets.resource?.map(r => ({
        ...r,
        title: r.id === artifactUpdate.id ? artifactUpdate.title : r.title,
      }));
  }

  private removeStoryArtifactFromWatched(storyId: string, artifactId: string) {
    this.watchedStoryArtifacts[storyId] = this.watchedStoryArtifacts[
      storyId
    ].filter(a => a.id !== artifactId);
    if (!this.watchedStoryArtifacts[storyId].length) {
      delete this.watchedStoryArtifacts[storyId];
    }
  }

  private removeOrganizationArtifactFromWatched(
    organizationId: string,
    artifactId: string,
  ) {
    this.watchedOrganizationArtifacts[organizationId] =
      this.watchedOrganizationArtifacts[organizationId].filter(
        a => a.id !== artifactId,
      );
    if (!this.watchedOrganizationArtifacts[organizationId].length) {
      delete this.watchedOrganizationArtifacts[organizationId];
    }
  }

  private updateWatchedStoryVideoArtifact(
    artifactUpdate: StoryArtifactsVideoUpdate['storyArtifactsVideo'][0],
  ) {
    if (!this.store.story?.storyArtifactsVideo) {
      return;
    }
    const artifactIdx = this.store.story.storyArtifactsVideo!.findIndex(
      a => a.id === artifactUpdate.id,
    );
    if (artifactIdx > -1) {
      this.store.story.storyArtifactsVideo =
        this.store.story.storyArtifactsVideo.map(a =>
          a.id === artifactUpdate.id
            ? { ...a, video: artifactUpdate.video }
            : a,
        );
    }
  }

  private updateWatchedOrganizationVideoArtifact(
    artifactUpdate: ShowcaseArtifactsVideoUpdate['organizationArtifactsVideo'][0],
  ) {
    if (!this.store.organization?.organizationArtifactsVideo) {
      return;
    }
    const artifactIdx =
      this.store.organization.organizationArtifactsVideo!.findIndex(
        a => a.id === artifactUpdate.id,
      );
    if (artifactIdx > -1) {
      this.store.organization.organizationArtifactsVideo =
        this.store.organization.organizationArtifactsVideo.map(a =>
          a.id === artifactUpdate.id
            ? { ...a, video: artifactUpdate.video }
            : a,
        );
    }
  }

  private removeStoryVideoArtifactFromWatched(
    storyId: string,
    artifactId: string,
  ) {
    this.watchedStoryVideoArtifacts[storyId] = this.watchedStoryVideoArtifacts[
      storyId
    ].filter(a => a.id !== artifactId);
    if (!this.watchedStoryVideoArtifacts[storyId].length) {
      delete this.watchedStoryVideoArtifacts[storyId];
    }
  }

  private removeOrganizationVideoArtifactFromWatched(
    organizationId: string,
    artifactId: string,
  ) {
    this.watchedOrganizationVideoArtifacts[organizationId] =
      this.watchedOrganizationVideoArtifacts[organizationId].filter(
        a => a.id !== artifactId,
      );
    if (!this.watchedOrganizationVideoArtifacts[organizationId].length) {
      delete this.watchedOrganizationVideoArtifacts[organizationId];
    }
  }
}

export default StatusUpdateManager;
